/**
 * Shared Collect.js inline field styling for Defera Capital (NMI).
 *
 * Collect.js only accepts a limited set of CSS properties inside iframes
 * (border-width, border-color, etc. — NOT the `border` shorthand). NMI's
 * defaults add an orange validation/focus ring on the iframe input; we keep
 * the visible shell on the outer mount div and force borderless inputs inside.
 */

/**
 * Visible shell around each Collect.js field. Collect.js overwrites inline
 * styles on the selector element itself, so the styled wrapper must be a
 * parent — the bare `#dc-nmi-*` div is left for Collect.js to own.
 */
export const NMI_FIELD_SHELL_CLASS =
  'h-11 min-h-[44px] w-full rounded-xl bg-white/[0.05] border border-white/[0.12] px-3.5 overflow-hidden box-border focus-within:border-white/30 transition-colors';

/** Collect.js-supported properties that remove inner iframe borders/rings. */
export const NMI_COLLECT_BORDERLESS = {
  'border-width': '0',
  'border-style': 'solid',
  'border-color': 'transparent',
  'border-radius': '0',
  'outline-width': '0',
  'outline-style': 'none',
  'outline-color': 'transparent',
  'box-shadow': 'none',
};

/** Applied inside Collect.js iframes to the <input>. */
export const NMI_COLLECT_CUSTOM_CSS = {
  'background-color': 'transparent',
  color: '#ffffff',
  'font-size': '16px',
  'line-height': '44px',
  padding: '0',
  'font-family': 'Inter, system-ui, sans-serif',
  'font-weight': '300',
  ...NMI_COLLECT_BORDERLESS,
};

/** No inner border on focus — outer mount div handles focus-within styling. */
export const NMI_COLLECT_FOCUS_CSS = {
  ...NMI_COLLECT_BORDERLESS,
};

/** Override NMI's default orange invalid border; text only turns red. */
export const NMI_COLLECT_INVALID_CSS = {
  color: '#f87171',
  ...NMI_COLLECT_BORDERLESS,
};

/** No green/orange valid-state ring inside the iframe. */
export const NMI_COLLECT_VALID_CSS = {
  ...NMI_COLLECT_BORDERLESS,
};

export const NMI_COLLECT_PLACEHOLDER_CSS = {
  color: 'rgba(255,255,255,0.4)',
  'font-weight': '300',
};

/** Parent-page CSS for Collect.js mount ids (pass comma-separated selectors). */
export function nmiCollectParentStyles(fieldSelectors) {
  return `
    ${fieldSelectors} {
      display: block;
      width: 100%;
      height: 100%;
      min-height: 44px;
      border: none;
      background: transparent;
      padding: 0;
      margin: 0;
    }
    ${fieldSelectors} iframe {
      display: block !important;
      width: 100% !important;
      height: 100% !important;
      min-height: 44px;
      border: 0 !important;
      outline: none !important;
      box-shadow: none !important;
      background: transparent !important;
    }
    ${fieldSelectors} .CollectJSInvalid iframe,
    ${fieldSelectors} .CollectJSValid iframe {
      border: 0 !important;
      outline: none !important;
      box-shadow: none !important;
    }
  `;
}

/** Full Collect.js configure() styling bundle. */
export function nmiCollectStyleConfig(overrides = {}) {
  return {
    customCss: { ...NMI_COLLECT_CUSTOM_CSS, ...overrides.customCss },
    focusCss: { ...NMI_COLLECT_FOCUS_CSS, ...overrides.focusCss },
    invalidCss: { ...NMI_COLLECT_INVALID_CSS, ...overrides.invalidCss },
    validCss: { ...NMI_COLLECT_VALID_CSS, ...overrides.validCss },
    placeholderCss: { ...NMI_COLLECT_PLACEHOLDER_CSS, ...overrides.placeholderCss },
  };
}
